import { useMemo } from 'react';
import moment from 'moment';
import { filterByDateRange, groupQueriesByCompany, filterCompaniesBySearch, createExpertMap, findExpertForQuery, getQueryDateForDisplay } from 'src/utils/queryUtils';

export const useQueryFiltering = (allQueries, experts, companiesWithActiveExperts, dateFilter, dateRanges, searchQuery) => {
  // Create expert lookup map once (O(1) lookups instead of O(n) finds)
  const expertMap = useMemo(() => createExpertMap(experts), [experts]);

  // Filter queries by date range and group by company
  const queriesByCompany = useMemo(() => {
    if (!experts || !allQueries?.length) return {};

    const selectedRange = dateRanges[dateFilter];

    // Filter queries by date range
    const filteredQueries = filterByDateRange(allQueries, selectedRange);

    // Group queries by company and their experts (pass expertMap for O(1) lookups)
    return groupQueriesByCompany(filteredQueries, experts, companiesWithActiveExperts, expertMap);
  }, [experts, allQueries, dateFilter, dateRanges, companiesWithActiveExperts, expertMap]);

  // Filter companies by search query
  const filteredQueriesByCompany = useMemo(() => {
    return filterCompaniesBySearch(queriesByCompany, searchQuery, companiesWithActiveExperts);
  }, [queriesByCompany, searchQuery, companiesWithActiveExperts]);

  // Get company-specific queries for a single company (used in ReportProfile)
  const getCompanyQueries = useMemo(() => {
    return (companyID, startDate, endDate) => {
      if (!experts || !allQueries?.length) return [];

      const selectedRange = {
        start: moment(startDate),
        end: moment(endDate),
      };

      return allQueries.filter((query) => {
        // Use expertMap for O(1) lookup
        const expert = findExpertForQuery(query, experts, expertMap);
        if (!expert) return false;

        const normalizedExpertCompany = expert.company.trim().toLowerCase().replace(/\+/g, '');
        const normalizedCompanyID = companyID.replaceAll('-', ' ').toLowerCase().replace(/\+/g, '');
        const companyMatches = normalizedExpertCompany === normalizedCompanyID;
        if (!companyMatches) return false;

        // Use consistent date logic with filterByDateRange (includes reportDate from notes)
        const dateToUse = moment(getQueryDateForDisplay(query));
        return dateToUse.isBetween(selectedRange.start, selectedRange.end, 'day', '[]');
      });
    };
  }, [experts, allQueries, expertMap]);

  // Group queries by expert for a specific company
  const getQueriesByExpert = useMemo(() => {
    return (companyQueries) => {
      if (!companyQueries || !experts) return {};

      return companyQueries.reduce((acc, query) => {
        // Use expertMap for O(1) lookup
        const expert = findExpertForQuery(query, experts, expertMap);
        if (!expert) return acc;

        if (!acc[expert.id]) {
          acc[expert.id] = {
            expert,
            queries: [],
          };
        }

        acc[expert.id].queries.push(query);
        return acc;
      }, {});
    };
  }, [experts, expertMap]);

  return {
    queriesByCompany,
    filteredQueriesByCompany,
    getCompanyQueries,
    getQueriesByExpert,
  };
};
